import { Capacitor, registerPlugin } from '@capacitor/core';
import { speak as sharedSpeak } from '@caro/shared/utils';
import { logEvent } from '../utils/debugLog';

/**
 * Wat de native TTS-engine terugmeldde. Tot 2026-09-24 kwam hier niets terug en kon een
 * tablet zonder werkende engine "voorgelezen" rapporteren terwijl hij stil bleef.
 */
export interface TtsSpeakResult {
  ready?: boolean;
  spoken?: boolean;
  speakResult?: number;
  langResult?: number;
  engine?: string;
  reason?: string;
  /** APK ≥ 1.0.6: het id waarmee het event `ttsDone` het einde van DEZE uiting meldt. */
  utteranceId?: string;
}

/**
 * Wat `KeepAliveBridge.speak()` de aanroeper vertelt (spraakmeldingen fase 0, FR-0.4).
 *
 *   spoken: true                    — de engine heeft gesproken (op een APK ≥ 1.0.6 en in de
 *                                     browser: tot het einde; op 1.0.5: de engine nam de
 *                                     zin aan, het einde is daar niet te kennen)
 *   spoken: false, reason no_voice  — geen engine of geen stem voor deze taal
 *   spoken: false, reason error     — de engine of de brug gooide
 */
export interface SpeakOutcome {
  spoken: boolean;
  reason?: 'no_voice' | 'error';
}

/** Waarvoor een native clip speelt: bepaalt de Android-`AudioAttributes` (volumeknop, DND). */
export type NativeAudioUsage = 'notification' | 'alarm';

interface NativeAudioEvent {
  playbackId?: string;
  key?: string;
  interrupted?: boolean;
  what?: number;
  extra?: number;
}

interface TtsDoneEvent {
  utteranceId?: string;
  status?: 'done' | 'error' | 'stopped';
}

/**
 * Ruwe native momentopname voor de batterijmeter. Alles optioneel: een oudere APK kent
 * `getPowerStats` niet, en een fabrikant kan elk veld weglaten. Normaliseren gebeurt in
 * `utils/powerStats.ts`, niet hier.
 */
export interface NativePowerStats {
  capacityPct?: number;
  levelPct?: number;
  currentNowRaw?: number;
  currentAvgRaw?: number;
  chargeCounterUah?: number;
  voltageMv?: number;
  temperatureDeciC?: number;
  plugged?: 'ac' | 'usb' | 'wireless' | 'none' | 'other';
  status?: 'charging' | 'discharging' | 'full' | 'not_charging' | 'unknown';
  systemBrightness?: number;
  autoBrightness?: boolean;
  windowBrightness?: number;
  keepScreenOn?: boolean;
  interactive?: boolean;
  powerSaveMode?: boolean;
  ignoringBatteryOptimizations?: boolean;
  versionName?: string;
  versionCode?: number;
  firstInstallTime?: number;
  lastUpdateTime?: number;
  installer?: string;
  androidRelease?: string;
  model?: string;
}

interface PermissionStates {
  microphone: string;
  camera: string;
  location: string;
  notifications: string;
}

interface KeepAlivePlugin {
  startService(): Promise<void>;
  stopService(): Promise<void>;
  wakeScreen(): Promise<void>;
  clearWake(): Promise<void>;
  openAppSettings(): Promise<void>;
  bringToFront(): Promise<void>;
  getDeviceMac(): Promise<{ mac: string }>;
  getDeviceIds(): Promise<{ androidId: string; managedUdid: string }>;
  getPowerStats(): Promise<NativePowerStats>;
  setWindowBrightness(options: { value: number; durationMs?: number }): Promise<void>;
  canDrawOverlays(): Promise<{ granted: boolean }>;
  requestOverlayPermission(): Promise<void>;
  checkPermissions(): Promise<PermissionStates>;
  requestPermissions(): Promise<PermissionStates>;
  speak(options: { text: string; lang?: string }): Promise<TtsSpeakResult | void>;
  stopTts(): Promise<void>;
  // ── APK ≥ 1.0.6 (versionCode 7, android-patches/0007-keepalive-play-audio.patch) ──
  cacheAudio(options: { key: string; base64: string }): Promise<{ path: string; bytes: number }>;
  playAudioFile(options: {
    key: string;
    usage: NativeAudioUsage;
    volume?: number;
  }): Promise<{ playbackId?: string; started?: boolean; durationMs?: number }>;
  stopAudio(): Promise<void>;
  hasAudio(options: { key: string }): Promise<{ exists: boolean }>;
  addListener(
    event: 'audioEnded' | 'audioError' | 'ttsDone',
    cb: (data: any) => void,
  ): Promise<{ remove: () => Promise<void> }>;
}

const KeepAlive = registerPlugin<KeepAlivePlugin>('KeepAlive');
const isNative = Capacitor.isNativePlatform();
let powerStatsWarned = false;

// ── Feature-detectie (spraakmeldingen fase 0, plan O-3) ─────────────────────
//
// ⚠ `typeof KeepAlive.playAudioFile === 'function'` is ALTIJD waar: de Capacitor-proxy geeft
// voor elke naam een functie terug, die pas bij aanroep "not implemented" gooit. Wat de APK
// echt kent, staat in de `PluginHeaders` die de native kant bij het opstarten in de WebView
// zet. Ontbreken die (onbekende brug, test, browser), dan is het antwoord "nee": het
// WebView-pad werkt overal, het native pad alleen waar het er aantoonbaar is.

const NATIVE_AUDIO_METHODS = ['cacheAudio', 'playAudioFile', 'stopAudio', 'hasAudio'] as const;

function hasNativeMethod(name: string): boolean {
  if (!isNative) return false;
  try {
    if (typeof Capacitor.isPluginAvailable === 'function' && !Capacitor.isPluginAvailable('KeepAlive')) {
      return false;
    }
    const headers = (Capacitor as unknown as {
      PluginHeaders?: Array<{ name?: string; methods?: Array<{ name?: string }> }>;
    }).PluginHeaders;
    if (!Array.isArray(headers)) return false;
    const header = headers.find((h) => h && h.name === 'KeepAlive');
    if (!header || !Array.isArray(header.methods)) return false;
    return header.methods.some((m) => m && m.name === name)
      && typeof (KeepAlive as unknown as Record<string, unknown>)[name] === 'function';
  } catch {
    return false;
  }
}

/**
 * Meldt de APK het einde van een uiting (`ttsDone`)? Dat event komt met dezelfde patch als
 * `playAudioFile` (1.0.6); een APK met de ene heeft de andere. Zonder het event resolve't de
 * native `speak` meteen na het aannemen van de zin, zoals vandaag.
 */
function nativeTtsReportsDone(): boolean {
  return hasNativeMethod('playAudioFile');
}

/** Bovengrens op het wachten op `ttsDone`: een engine die het event nooit stuurt, mag niet
 *  eeuwig een belofte openhouden. De wachtrij heeft daarnaast haar eigen time-out. */
function ttsWaitCapMs(text: string): number {
  return Math.min(60_000, 5_000 + String(text || '').length * 150);
}

// Eén luisteraar per event voor de hele sessie, niet één per afspeelbeurt. De events kunnen
// binnenkomen VÓÓR het antwoord op `playAudioFile`/`speak` (een korte clip, een snelle
// engine), dus wat te vroeg komt wordt even onthouden.
const EARLY_MAX = 50;
let nativeListenersInstalled = false;
const playbackWaiters = new Map<string, { resolve: () => void; reject: (e: Error) => void }>();
const earlyPlaybackEnds = new Map<string, 'ended' | 'error'>();
const utteranceWaiters = new Map<string, () => void>();
const earlyUtteranceEnds = new Set<string>();

function trim(store: Map<string, unknown> | Set<string>) {
  if (store.size <= EARLY_MAX) return;
  const first = store.keys().next().value as string;
  store.delete(first);
}

function settlePlayback(data: NativeAudioEvent | null | undefined, kind: 'ended' | 'error') {
  const id = data && data.playbackId ? String(data.playbackId) : '';
  if (!id) return;
  const waiter = playbackWaiters.get(id);
  if (!waiter) {
    earlyPlaybackEnds.set(id, kind);
    trim(earlyPlaybackEnds);
    return;
  }
  playbackWaiters.delete(id);
  if (kind === 'ended') waiter.resolve();
  else waiter.reject(new Error(`native audio error what=${data?.what} extra=${data?.extra}`));
}

function settleUtterance(data: TtsDoneEvent | null | undefined) {
  const id = data && data.utteranceId ? String(data.utteranceId) : '';
  if (!id) return;
  const waiter = utteranceWaiters.get(id);
  if (!waiter) {
    earlyUtteranceEnds.add(id);
    trim(earlyUtteranceEnds);
    return;
  }
  utteranceWaiters.delete(id);
  waiter();
}

function ensureNativeListeners() {
  if (nativeListenersInstalled) return;
  nativeListenersInstalled = true;
  try {
    void KeepAlive.addListener('audioEnded', (d) => settlePlayback(d, 'ended'));
    void KeepAlive.addListener('audioError', (d) => settlePlayback(d, 'error'));
    void KeepAlive.addListener('ttsDone', (d) => settleUtterance(d));
  } catch (e) {
    console.warn('[KeepAlive] addListener failed:', e);
  }
}

function waitForUtterance(id: string, capMs: number): Promise<void> {
  if (earlyUtteranceEnds.has(id)) {
    earlyUtteranceEnds.delete(id);
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    const timer = setTimeout(() => {
      utteranceWaiters.delete(id);
      resolve();
    }, capMs);
    utteranceWaiters.set(id, () => {
      clearTimeout(timer);
      resolve();
    });
  });
}

/** `stopTts()` laat elke wachtende `speak()` los: er komt niets meer. */
function releaseUtteranceWaiters() {
  const all = Array.from(utteranceWaiters.values());
  utteranceWaiters.clear();
  for (const w of all) w();
}

/** Een Blob als kale base64 (zonder `data:`-prefix). FileReader: ook op Chromium 69. */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => {
        const s = String(reader.result || '');
        const comma = s.indexOf(',');
        resolve(comma >= 0 ? s.slice(comma + 1) : s);
      };
      reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
      reader.readAsDataURL(blob);
    } catch (e) {
      reject(e);
    }
  });
}

export const KeepAliveBridge = {
  async startService() {
    if (!isNative) return;
    try {
      await KeepAlive.startService();
    } catch (e) {
      console.warn('[KeepAlive] startService failed:', e);
    }
  },

  async stopService() {
    if (!isNative) return;
    try {
      await KeepAlive.stopService();
    } catch (e) {
      console.warn('[KeepAlive] stopService failed:', e);
    }
  },

  async wakeScreen() {
    if (!isNative) return;
    try {
      await KeepAlive.wakeScreen();
    } catch (e) {
      console.warn('[KeepAlive] wakeScreen failed:', e);
    }
  },

  async clearWake() {
    if (!isNative) return;
    try {
      await KeepAlive.clearWake();
    } catch (e) {
      console.warn('[KeepAlive] clearWake failed:', e);
    }
  },

  async openAppSettings() {
    if (!isNative) return;
    try {
      await KeepAlive.openAppSettings();
    } catch (e) {
      console.warn('[KeepAlive] openAppSettings failed:', e);
    }
  },

  async bringToFront() {
    if (!isNative) return;
    try {
      await KeepAlive.bringToFront();
    } catch (e) {
      console.warn('[KeepAlive] bringToFront failed:', e);
    }
  },

  async getDeviceMac(): Promise<string> {
    if (!isNative) return 'web-browser';
    try {
      const result = await KeepAlive.getDeviceMac();
      return result.mac;
    } catch (e) {
      console.warn('[KeepAlive] getDeviceMac failed:', e);
      return 'unknown';
    }
  },

  // Join-key diagnostics: this APK's own ANDROID_ID vs the MDM-pushed managed
  // config value. Used to decide the hands-free provisioning path.
  async getDeviceIds(): Promise<{ androidId: string; managedUdid: string }> {
    if (!isNative) return { androidId: 'web', managedUdid: '' };
    try {
      return await KeepAlive.getDeviceIds();
    } catch (e) {
      console.warn('[KeepAlive] getDeviceIds failed:', e);
      return { androidId: 'error', managedUdid: '' };
    }
  },

  /**
   * Batterij/scherm/APK-versie. `null` in de browser of op een APK van vóór 1.0.4
   * (versionCode 5) — die kent de methode niet, en dat is zelf informatie.
   */
  async getPowerStats(): Promise<NativePowerStats | null> {
    if (!isNative) return null;
    try {
      return await KeepAlive.getPowerStats();
    } catch (e) {
      // "not implemented" = oudere APK; iets anders = een fout in de native methode.
      // Eén keer per sessie in het diagnoselogboek, anders elke 15 s.
      if (!powerStatsWarned) {
        powerStatsWarned = true;
        logEvent('power.native_unavailable', { error: e instanceof Error ? e.message : String(e) });
      }
      return null;
    }
  },

  /**
   * Echte schermverlichting van dit venster: `value` 0..1, vloeiend over `durationMs`;
   * een negatieve waarde geeft de helderheid terug aan de systeeminstelling. Doet niets
   * in de browser of op een APK < 1.0.5 (versionCode 6) — die dimmen alleen met CSS.
   */
  async setWindowBrightness(value: number, durationMs = 0): Promise<boolean> {
    if (!isNative) return false;
    try {
      await KeepAlive.setWindowBrightness({ value, durationMs });
      return true;
    } catch {
      /* oudere APK: geen native helderheid */
      return false;
    }
  },

  async canDrawOverlays(): Promise<boolean> {
    if (!isNative) return true;
    try {
      const result = await KeepAlive.canDrawOverlays();
      return result.granted;
    } catch (e) {
      console.warn('[KeepAlive] canDrawOverlays failed:', e);
      return true;
    }
  },

  async requestOverlayPermission() {
    if (!isNative) return;
    try {
      await KeepAlive.requestOverlayPermission();
    } catch (e) {
      console.warn('[KeepAlive] requestOverlayPermission failed:', e);
    }
  },

  // ── Native afspelen (APK ≥ 1.0.6) ────────────────────────────────────────
  //
  // Spraakmeldingen fase 0 (FR-0.2/FR-0.3, plan O-3). `MediaPlayer` speelt zonder de
  // autoplaybeperkingen van de WebView. Elke methode hieronder is veilig op een oudere APK:
  // `supportsNativeAudio()` zegt nee, en `services/audioItems.ts` kiest dan het WebView-pad.

  /** Kent deze APK `cacheAudio`/`playAudioFile`/`stopAudio`/`hasAudio`? */
  supportsNativeAudio(): boolean {
    return NATIVE_AUDIO_METHODS.every((m) => hasNativeMethod(m));
  },

  /** Staat `<key>.mp3` al in de native cache? `false` bij elke twijfel. */
  async hasAudio(key: string): Promise<boolean> {
    if (!KeepAliveBridge.supportsNativeAudio()) return false;
    try {
      const r = await KeepAlive.hasAudio({ key });
      return !!(r && r.exists);
    } catch {
      return false;
    }
  },

  /** Schrijf de bytes naar `getCacheDir()/voices/<key>.mp3`. Gooit bij een fout. */
  async cacheAudioBlob(key: string, blob: Blob): Promise<void> {
    if (!KeepAliveBridge.supportsNativeAudio()) throw new Error('native audio unavailable');
    const base64 = await blobToBase64(blob);
    await KeepAlive.cacheAudio({ key, base64 });
  },

  /**
   * Speel `<key>.mp3` uit de native cache. Resolve't pas op het EINDE (`audioEnded`), rejecte
   * op `audioError` of als de native kant weigert. `onStarted` komt zodra de speler loopt —
   * dat is het moment dat `soundPlayed: true` waar wordt.
   */
  async playAudioFile(
    key: string,
    opts: { usage?: NativeAudioUsage; volume?: number; onStarted?: () => void } = {},
  ): Promise<void> {
    if (!KeepAliveBridge.supportsNativeAudio()) throw new Error('native audio unavailable');
    ensureNativeListeners();
    const res = await KeepAlive.playAudioFile({
      key,
      usage: opts.usage || 'notification',
      ...(typeof opts.volume === 'number' ? { volume: opts.volume } : {}),
    });
    const playbackId = res && res.playbackId ? String(res.playbackId) : '';
    if (opts.onStarted) {
      try {
        opts.onStarted();
      } catch {
        /* een luisteraar mag het afspelen niet stoppen */
      }
    }
    // Zonder id valt het einde niet te koppelen. Dan geldt de time-out van de wachtrij.
    if (!playbackId) return new Promise<void>(() => {});
    const early = earlyPlaybackEnds.get(playbackId);
    if (early) {
      earlyPlaybackEnds.delete(playbackId);
      if (early === 'ended') return undefined;
      throw new Error('native audio error');
    }
    return new Promise<void>((resolve, reject) => {
      playbackWaiters.set(playbackId, { resolve, reject });
    });
  },

  /** Stop de native speler. Werpt nooit. */
  async stopAudio(): Promise<void> {
    if (!KeepAliveBridge.supportsNativeAudio()) return;
    try {
      await KeepAlive.stopAudio();
    } catch (e) {
      console.warn('[KeepAlive] stopAudio failed:', e);
    }
  },

  /**
   * Spreek tekst uit met de Android-TTS; in de browser met Web Speech.
   *
   * Resolve't pas wanneer de zin UIT is: in de browser op `onend` (`@caro/shared/utils`), op
   * een APK ≥ 1.0.6 op het event `ttsDone` voor deze uiting. Een APK 1.0.5 meldt het einde
   * niet; daar resolve't hij zoals vroeger meteen nadat de engine de zin aannam. Werpt nooit.
   */
  async speak(text: string, lang: string = 'nl'): Promise<SpeakOutcome> {
    if (!isNative) {
      // Fallback to shared Web Speech API utility on browser / desktop.
      // Routes through `@caro/shared/utils.speak` so there is exactly one
      // TTS implementation in the resident-app (T3.2 / F3-min.5).
      try {
        await sharedSpeak(text, { lang });
        return { spoken: true };
      } catch (e) {
        console.warn('[KeepAlive] Web Speech fallback failed:', e);
        return { spoken: false, reason: 'error' };
      }
    }
    const waitsForDone = nativeTtsReportsDone();
    if (waitsForDone) ensureNativeListeners();
    try {
      const result = (await KeepAlive.speak({ text, lang })) as TtsSpeakResult | void;
      // Op de diagnose-overlay, zodat "ik hoor geen stem" een meetbare oorzaak krijgt:
      // geen engine (`ready: false`), taal niet geïnstalleerd (`langResult` < 0) of een
      // geweigerde `speak()` (`spoken: false`). Een leeg resultaat is een oudere APK.
      if (result && result.spoken === false) {
        logEvent('tts:silent', { lang, ...result });
        console.warn('[KeepAlive] TTS did not speak:', result);
        // 20261016-taal-bewonersapp R-4: geen stem voor deze taal (fr/de op een tablet zonder
        // die stem) is een eigen, telbare regel voor T041 — geen stille fout.
        if (typeof result.langResult === 'number' && result.langResult < 0) {
          console.warn('[TTS] no voice', lang);
          logEvent('tts.noVoice', { lang });
        }
        return { spoken: false, reason: 'no_voice' };
      }
      // T053a (eigenaarstest 2026-10-25): de APK logt LANG_MISSING_DATA / LANG_NOT_SUPPORTED,
      // spreekt tóch en meldt `spoken: true`. Een ontbrekende taal is geen gesproken zin: eerlijk
      // `no_voice`, zodat de ack `voicePlayed: false` / `voiceReason: no_audio` meldt. Wat de
      // engine wel uitspreekt laten we eerst uitlopen (geen overlap met het volgende item).
      const langMissing = !!result && typeof result.langResult === 'number' && result.langResult < 0;
      if (langMissing) {
        console.warn('[TTS] no voice', lang);
        logEvent('tts.noVoice', { lang });
      } else {
        logEvent('tts:spoken', { lang, ...(result || { legacy: true }) });
      }
      if (waitsForDone && result && result.utteranceId) {
        await waitForUtterance(String(result.utteranceId), ttsWaitCapMs(text));
      }
      if (langMissing) return { spoken: false, reason: 'no_voice' };
      return { spoken: true };
    } catch (e) {
      logEvent('tts:error', { lang, error: e instanceof Error ? e.message : String(e) });
      console.warn('[KeepAlive] speak failed:', e);
      return { spoken: false, reason: 'error' };
    }
  },

  async stopTts(): Promise<void> {
    // Wie nog op het einde van een zin wacht, krijgt het nu: er komt niets meer.
    releaseUtteranceWaiters();
    if (!isNative) {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
      return;
    }
    try {
      await KeepAlive.stopTts();
    } catch (e) {
      console.warn('[KeepAlive] stopTts failed:', e);
    }
  },

  /**
   * Check all Android permission states natively.
   * Returns { microphone, camera, location, notifications } with values
   * "granted", "prompt", or "denied".
   */
  async checkPermissions(): Promise<PermissionStates | null> {
    if (!isNative) return null;
    try {
      return await KeepAlive.checkPermissions();
    } catch (e) {
      console.warn('[KeepAlive] checkPermissions failed:', e);
      return null;
    }
  },

  /**
   * Request all app permissions via Android's native permission dialog.
   * Returns the updated permission states after the user responds.
   */
  async requestPermissions(): Promise<PermissionStates | null> {
    if (!isNative) return null;
    try {
      return await KeepAlive.requestPermissions();
    } catch (e) {
      console.warn('[KeepAlive] requestPermissions failed:', e);
      return null;
    }
  },
};
